import './abrechnung.css';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { VertragAngaben } from '../vertrag/Vertrag';
import { RechnungSchreiben } from './RechnungSchreiben';
import type { AbrechnungResponse, BillingItem, OfficeSettings, PriceRow } from './types';

// Abrechnung (nur Admin, Sam 11.10.): Was die Kanzlei den Mandanten berechnen kann. Setzt die FiBu einen Monat auf
// „gebucht“ und hat der Mandant einen Monatsbetrag (Stammdaten → Betreuung), steht der Monat hier unter „Zu berechnen“;
// erledigte Unterlagen mit Preis kommen dazu. „Rechnung schreiben“ macht daraus eine Rechnung (PDF) an den Mandanten.
// Darunter die Angaben auf der Rechnung (Büro, Steuernummer, Bank) und die Preise für angeforderte Unterlagen.

export function AbrechnungPage() {
  const res = useApi<AbrechnungResponse>('/api/kanzlei/abrechnung', { live: ['abrechnung', 'clients', 'invoices'] });
  const [write, setWrite] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    document.title = 'Abrechnung · Elbcons Kanzlei';
  }, []);

  const d = res.data;
  return (
    <div className="abr">
      <div className="head">
        <div>
          <h1>Abrechnung</h1>
          <p>Rechnungen an Mandanten: gebuchte Monate und erledigte Unterlagen.</p>
        </div>
      </div>
      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          {!d.settings && (
            <div className="lock">
              Bitte zuerst unten die Angaben für die Rechnung eintragen (Name, Anschrift, Steuernummer, Bank). Sie stehen auf
              jeder Rechnung.
            </div>
          )}
          <Offen data={d} canWrite={!!d.settings} onWrite={setWrite} />
          <div className="grid2">
            <Angaben settings={d.settings} reload={res.reload} />
            <Preise prices={d.prices} reload={res.reload} />
          </div>
          <VertragAngaben />
        </>
      )}
      {write && <RechnungSchreiben clientId={write.id} clientName={write.name} onClose={() => setWrite(null)} />}
    </div>
  );
}

// „Zu berechnen“: offene Positionen je Mandant mit „Rechnung schreiben“ und „Nicht berechnen“
function Offen({
  data,
  canWrite,
  onWrite,
}: {
  data: AbrechnungResponse;
  canWrite: boolean;
  onWrite(c: { id: string; name: string }): void;
}) {
  const toast = useToast();
  const [ask, setAsk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const total = data.open.reduce((s, g) => s + g.net, 0);

  const discard = async (i: BillingItem) => {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/abrechnung/item/${i.id}/discard`);
      setAsk(null);
      toast(`„${i.description}“ wird nicht berechnet`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card abr-open-card" aria-labelledby="abr-open-h">
      <div className="ch">
        <h3 id="abr-open-h">Zu berechnen</h3>
        {data.open.length > 0 && (
          <span>
            {data.open.length} {data.open.length === 1 ? 'Mandant' : 'Mandanten'} · <b>{eurC(total)}</b> netto
          </span>
        )}
      </div>
      {data.open.length === 0 ? (
        <p className="note abr-m0">
          Gerade nichts zu berechnen. Ein Vorschlag kommt, sobald die FiBu einen Monat auf „gebucht“ setzt (beim Mandanten muss
          unter Stammdaten → Betreuung der Monatsbetrag eingetragen sein) oder eine angeforderte Unterlage mit Preis erledigt ist.
        </p>
      ) : (
        <div className="abr-groups">
          {data.open.map((g) => (
            <div className="abr-g" key={g.client.id}>
              <div className="abr-gh">
                <div className="abr-gn">
                  <b>{g.client.name}</b>
                  {g.client.number && <span className="note">Kd.-Nr. {g.client.number}</span>}
                </div>
                <span className="num abr-gs">{eurC(g.net)} netto</span>
                <button
                  type="button"
                  className="btn sm accent"
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Erst die Angaben für die Rechnung eintragen'}
                  onClick={() => onWrite({ id: g.client.id, name: g.client.name })}
                >
                  Rechnung schreiben
                </button>
              </div>
              <ul className="abr-items">
                {g.items.map((i) => (
                  <li key={i.id}>
                    <div className="abr-it">
                      <span>{i.description}</span>
                      <span className="num">{eurC(i.net)}</span>
                      {ask !== i.id && (
                        <button type="button" className="abr-link" onClick={() => setAsk(i.id)}>
                          Nicht berechnen
                        </button>
                      )}
                    </div>
                    {ask === i.id && (
                      <div className="confirmup abr-ask">
                        <span className="note">
                          {i.kind === 'monat'
                            ? 'Diesen Monat nicht berechnen (z. B. schon anders abgerechnet)? Er kommt dann nicht wieder als Vorschlag.'
                            : i.kind === 'post'
                              ? 'Diese Briefe nicht berechnen? Sie kommen dann nicht wieder als Vorschlag.'
                              : 'Diese Unterlage nicht berechnen?'}
                        </span>
                        <div className="abr-btns">
                          <button type="button" className="btn sm dark" disabled={busy} onClick={() => void discard(i)}>
                            Ja, nicht berechnen
                          </button>
                          <button type="button" className="btn sm" onClick={() => setAsk(null)}>
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Angaben auf der Rechnung (Pflichtangaben nach § 14 UStG): Büro, Steuernummer oder USt-IdNr., Bank, Umsatzsteuer
type Form = {
  name: string;
  street: string;
  zip: string;
  city: string;
  email: string;
  phone: string;
  taxNumber: string;
  vatId: string;
  accountHolder: string;
  iban: string;
  bic: string;
  bankName: string;
  vatRate: 19 | 0;
  paymentDays: string;
};

const ibanText = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

function formOf(s: OfficeSettings | null): Form {
  return {
    name: s?.name ?? '',
    street: s?.street ?? '',
    zip: s?.zip ?? '',
    city: s?.city ?? '',
    email: s?.email ?? '',
    phone: s?.phone ?? '',
    taxNumber: s?.taxNumber ?? '',
    vatId: s?.vatId ?? '',
    accountHolder: s?.accountHolder ?? '',
    iban: s ? ibanText(s.iban) : '',
    bic: s?.bic ?? '',
    bankName: s?.bankName ?? '',
    vatRate: s?.vatRate ?? 19,
    paymentDays: String(s?.paymentDays ?? 14),
  };
}

function Angaben({ settings, reload }: { settings: OfficeSettings | null; reload(): Promise<void> }) {
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<Form>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f: Form = { ...formOf(settings), ...draft };
  const set = (v: Partial<Form>) => {
    setDraft((x) => ({ ...x, ...v }));
    setError(null);
  };
  const field = (
    key: keyof Omit<Form, 'vatRate'>,
    label: string,
    extra: { max?: number; mode?: 'numeric' | 'email' | 'tel' } = {},
  ) => (
    <label>
      {label}
      <input
        value={f[key]}
        maxLength={extra.max ?? 120}
        inputMode={extra.mode}
        type={extra.mode === 'email' ? 'email' : extra.mode === 'tel' ? 'tel' : 'text'}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </label>
  );

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const days = f.paymentDays.trim();
    setBusy(true);
    try {
      await api.put('/api/kanzlei/abrechnung/settings', {
        name: f.name,
        street: f.street,
        zip: f.zip,
        city: f.city,
        email: f.email,
        phone: f.phone,
        taxNumber: f.taxNumber,
        vatId: f.vatId,
        accountHolder: f.accountHolder,
        iban: f.iban,
        bic: f.bic,
        bankName: f.bankName,
        vatRate: f.vatRate,
        paymentDays: /^\d{1,2}$/.test(days) ? Number(days) : -1,
      });
      setDraft({});
      await reload();
      toast('Angaben für die Rechnung gespeichert');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="abr-set-h">
      <div className="ch">
        <h3 id="abr-set-h">Angaben auf der Rechnung</h3>
        <span>stehen auf jeder Rechnung</span>
      </div>
      <form className="form" onSubmit={save} noValidate>
        {field('name', 'Name des Büros *')}
        <div className="row abr-addr">
          {field('street', 'Straße und Hausnummer *')}
          {field('zip', 'PLZ *', { max: 5, mode: 'numeric' })}
          {field('city', 'Ort *')}
        </div>
        <div className="row abr-two">
          {field('email', 'E-Mail', { mode: 'email' })}
          {field('phone', 'Telefon', { max: 40, mode: 'tel' })}
        </div>
        <div className="row abr-two">
          {field('taxNumber', 'Steuernummer', { max: 40 })}
          {field('vatId', 'USt-IdNr.', { max: 20 })}
        </div>
        <p className="note abr-m0">Steuernummer oder USt-IdNr.: eins von beiden muss auf jeder Rechnung stehen.</p>
        <div className="row abr-two">
          {field('accountHolder', 'Kontoinhaber *')}
          {field('iban', 'IBAN *', { max: 60 })}
        </div>
        <div className="row abr-two">
          {field('bic', 'BIC', { max: 11 })}
          {field('bankName', 'Bank')}
        </div>
        <div className="grp" role="group" aria-labelledby="abr-vat-l">
          <span id="abr-vat-l">Umsatzsteuer</span>
          <span className="seg">
            <button type="button" aria-pressed={f.vatRate === 19} onClick={() => set({ vatRate: 19 })}>
              19 %
            </button>
            <button type="button" aria-pressed={f.vatRate === 0} onClick={() => set({ vatRate: 0 })}>
              Kleinunternehmer (§ 19)
            </button>
          </span>
          <span className="note">
            Die Beträge (Monatsbetrag, Preise) sind netto, die Umsatzsteuer kommt auf der Rechnung dazu.
          </span>
        </div>
        <label className="abr-days">
          Zahlungsziel in Tagen *
          <input value={f.paymentDays} inputMode="numeric" maxLength={2} onChange={(e) => set({ paymentDays: e.target.value })} />
        </label>
        {error && (
          <p className="note abr-err" role="alert">
            {error}
          </p>
        )}
        <div>
          <button type="submit" className="btn dark" disabled={busy}>
            Speichern
          </button>
        </div>
      </form>
    </section>
  );
}

// Preise für angeforderte Unterlagen (netto). Leer = kostenlos. Erledigt die Kanzlei eine Unterlage mit Preis,
// kommt sie unter „Zu berechnen“ und auf die nächste Rechnung.
const amountText = (v: number) => v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Preise({ prices, reload }: { prices: PriceRow[]; reload(): Promise<void> }) {
  const toast = useToast();
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const value = (p: PriceRow) => edit[p.label] ?? (p.price === null ? '' : amountText(p.price));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const list: { label: string; price: number | null }[] = [];
    for (const p of prices) {
      const v = parseEuro(value(p));
      if (v === undefined || (v !== null && (v < 0 || v >= 1e6))) {
        setError(`Bitte den Preis für „${p.label}“ prüfen, z. B. 25,00.`);
        return;
      }
      list.push({ label: p.label, price: v ? v : null });
    }
    setBusy(true);
    try {
      await api.put('/api/kanzlei/abrechnung/prices', { prices: list });
      setEdit({});
      await reload();
      toast('Preise gespeichert');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const group = (g: PriceRow['group'], title: string) => (
    <div className="abr-pg">
      <h4>{title}</h4>
      {prices
        .filter((p) => p.group === g)
        .map((p) => (
          <label key={p.label} className="abr-price">
            <span>{p.label}</span>
            <input
              className="num"
              value={value(p)}
              inputMode="decimal"
              placeholder="kostenlos"
              onChange={(e) => {
                setEdit((x) => ({ ...x, [p.label]: e.target.value }));
                setError(null);
              }}
            />
          </label>
        ))}
    </div>
  );

  return (
    <section className="card" aria-labelledby="abr-pr-h">
      <div className="ch">
        <h3 id="abr-pr-h">Preise für Unterlagen</h3>
        <span>€ netto · leer = kostenlos</span>
      </div>
      <form className="form" onSubmit={save} noValidate>
        {group('firma', 'Firma')}
        {group('mitarbeiter', 'Zu Mitarbeitern')}
        <p className="note abr-m0">
          Erledigt die Kanzlei eine angeforderte Unterlage mit Preis, steht sie unter „Zu berechnen“ und kommt auf die nächste
          Rechnung. Neue Preise gelten für Unterlagen, die ab jetzt erledigt werden.
        </p>
        {error && (
          <p className="note abr-err" role="alert">
            {error}
          </p>
        )}
        <div>
          <button type="submit" className="btn dark" disabled={busy}>
            Preise speichern
          </button>
        </div>
      </form>
    </section>
  );
}
